// Helpers for calendar events and recurring event series

import type {
  EventFeatureFilter,
  EventRecurrenceRule,
  EventRecurrenceType,
  EventSeries,
  EventWeekOrdinal,
  FeatureType,
  RFC3339Date
} from '@/models';
import {
  EVENT_RECURRENCE_TYPE_LABELS,
  EVENT_WEEKDAY_LABELS,
  ALL_EVENT_WEEKDAYS,
  ALL_EVENT_WEEK_ORDINALS
} from '@/models';
import type { UpdateEventSeriesRequest } from '@/modelsAPI';
import { formatRFCDate } from '@/utils/helpers';

// Hard limit of the backend (models/eventSeries.go: MaxEventSeriesOccurrences)
export const MAX_EVENT_SERIES_OCCURRENCES = 500;

// The exact csv header the import expects (models/eventImport.go).
// The 8th column "Erinnerungen" is optional - 7-column files still import.
export const EVENT_IMPORT_CSV_HEADER = 'Eventtyp;Wochentag;Datum;Beginn;Ende;Google Meet Link;Zielgruppe;Erinnerungen';

// ---------------------------------------------------------------------------
// Accepted values of the csv "Zielgruppe" column.
// MUST BE SYNCED WITH THE BACKEND: models/eventImport.go
//   - eventAudienceLabels           -> EVENT_IMPORT_AUDIENCE_ALIASES
//   - eventAudiencePublicValues     -> first entry of EVENT_IMPORT_SPECIAL_AUDIENCES
//   - eventAudienceExcludeCommunityValues -> second entry
// Typing the alias map as Record<FeatureType, ...> means a new FeatureType breaks the
// build until it gets an alias here - the same guarantee the backend gets from
// TestParseEventAudienceResolvesEveryFeatureType.
// ---------------------------------------------------------------------------

// Whole-cell values that don't resolve to a feature
export const EVENT_IMPORT_SPECIAL_AUDIENCES: { values: string[]; meaning: string }[] = [
  {
    values: ['(leer)', 'Alle', 'Alle Mitglieder', 'Öffentlich', 'Oeffentlich', 'public'],
    meaning: 'Für alle Nutzer sichtbar'
  },
  {
    values: ['Alle außer Community', 'Alle ausser Community', 'Alle außer Community-Mitglieder', 'Alle ausser Community-Mitglieder'],
    meaning: 'Alle außer Community-Mitgliedern'
  },
  {
    values: ['Nur Community-Mitglieder'],
    meaning: 'Ausschließlich Community-Mitglieder'
  },
];

// Every spelling the backend resolves to a feature, per feature type
export const EVENT_IMPORT_AUDIENCE_ALIASES: Record<FeatureType, string[]> = {
  'CORE_RECOMMENDATIONS':       ['core', 'kern', 'kernempfehlungen', 'core-empfehlungen'],
  'EARNING_RECOMMENDATIONS':    ['earnings', 'earning', 'quartalszahlen'],
  'SWINGTRADE_RECOMMENDATIONS': ['swingtrade', 'swingtrades', 'swing', 'swing-trade'],
  'LONGTERM_RECOMMENDATIONS':   ['longterm', 'long-term', 'langfrist', 'langfristig'],
  'CHATBOT':                    ['chatbot'],
  'VIDEO_CHATBOT':              ['videochatbot', 'video-chatbot'],
  'JOURNAL':                    ['journal', 'tagebuch'],
  'BOOST':                      ['boost'],
  'COMMUNITY_MEMBER':           ['community'],
  'DAY_TRADING':                ['daytrading', 'day-trading', 'daytrade'],
  'OPTION_SCREENER':            ['optionscreener', 'optionsscreener', 'option-screener'],
  'VIDEOSEARCH':                ['videosearch', 'video-search', 'videosuche', 'video-suche', 'videofinder', 'video-finder'],
};

// Prefixes the backend strips before matching (models/eventImport.go: stripAudiencePrefix)
export const EVENT_IMPORT_AUDIENCE_PREFIXES = ['Nur Mitglieder mit Tag:', 'Nur Mitglieder mit Tags:', 'Nur für', 'Nur'];

// Compound suffixes the backend strips (models/eventImport.go: normalizeAudienceToken)
export const EVENT_IMPORT_AUDIENCE_SUFFIXES = ['-Mitglieder', '-Mitglied', '-Tag', '-Tags'];

// Separators that combine several features into one cell (OR-joined)
export const EVENT_IMPORT_AUDIENCE_SEPARATORS = ['und', 'oder', ',', '+', '/', '&'];

// Which rule fields a recurrence type allows. The backend rejects any field that is
// not allowed for the selected type (models/eventSeries.go: EventRecurrenceRule.Validate),
// so the editor must clear them on a type switch.
interface RecurrenceFieldSet {
  weekdays: boolean;
  ordinals: boolean;
  daysOfMonth: boolean;
}

export const ALLOWED_RECURRENCE_FIELDS: Record<EventRecurrenceType, RecurrenceFieldSet> = {
  'WEEKLY':               { weekdays: true,  ordinals: false, daysOfMonth: false },
  'MONTHLY_NTH_WEEKDAY':  { weekdays: true,  ordinals: true,  daysOfMonth: false },
  'MONTHLY_DAY_OF_MONTH': { weekdays: false, ordinals: false, daysOfMonth: true },
  'YEARLY':               { weekdays: false, ordinals: false, daysOfMonth: false },
};

// Returns a rule with every field the new type forbids cleared out
export function normalizeRecurrenceRule(rule: EventRecurrenceRule): EventRecurrenceRule {
  const allowed = ALLOWED_RECURRENCE_FIELDS[rule.type];

  return {
    type: rule.type,
    interval: rule.interval,
    weekdays: allowed.weekdays ? rule.weekdays : [],
    ordinals: allowed.ordinals ? rule.ordinals : [],
    daysOfMonth: allowed.daysOfMonth ? rule.daysOfMonth : [],
  };
}

// Returns a German validation message, or null if the rule is valid.
// Mirrors EventRecurrenceRule.Validate() so the user gets feedback before the request.
export function validateRecurrenceRule(rule: EventRecurrenceRule): string | null {
  if (rule.interval < 1 || rule.interval > 52) return 'Das Intervall muss zwischen 1 und 52 liegen';

  const allowed = ALLOWED_RECURRENCE_FIELDS[rule.type];

  if (allowed.weekdays && rule.weekdays.length === 0) return 'Bitte mindestens einen Wochentag auswählen';
  if (allowed.ordinals && rule.ordinals.length === 0) return 'Bitte mindestens einen Ordinal-Wert auswählen (z.B. „Erster")';
  if (allowed.daysOfMonth && rule.daysOfMonth.length === 0) return 'Bitte mindestens einen Tag im Monat auswählen';

  if (rule.daysOfMonth.some(d => d < 1 || d > 31)) return 'Tage im Monat müssen zwischen 1 und 31 liegen';

  return null;
}

// Sorts weekdays/ordinals into their natural order instead of click order
function sortWeekdays(rule: EventRecurrenceRule): string[] {
  return ALL_EVENT_WEEKDAYS.filter(d => rule.weekdays.includes(d)).map(d => EVENT_WEEKDAY_LABELS[d]);
}

// Used attributively in front of the weekday: "Jeden letzten Mittwoch im Monat"
const ORDINAL_ATTRIBUTIVE_LABELS: Record<EventWeekOrdinal, string> = {
  'FIRST':  '1.',
  'SECOND': '2.',
  'THIRD':  '3.',
  'FOURTH': '4.',
  'LAST':   'letzten',
};

// Used in front of the neuter noun "Vorkommen": "1. und letztes Vorkommen"
const ORDINAL_NOUN_LABELS: Record<EventWeekOrdinal, string> = {
  'FIRST':  '1.',
  'SECOND': '2.',
  'THIRD':  '3.',
  'FOURTH': '4.',
  'LAST':   'letztes',
};

function sortOrdinals(rule: EventRecurrenceRule, labels: Record<EventWeekOrdinal, string>): string[] {
  return ALL_EVENT_WEEK_ORDINALS.filter(o => rule.ordinals.includes(o)).map(o => labels[o]);
}

function joinGerman(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

// Human-readable German summary of a recurrence rule, e.g.
// "Jeden ersten Mittwoch im Monat, bis 02.03.2027"
export function describeRecurrenceRule(rule: EventRecurrenceRule, until?: RFC3339Date | null): string {
  const n = rule.interval;
  let text: string;

  switch (rule.type) {
    case 'WEEKLY': {
      const days = joinGerman(sortWeekdays(rule));
      text = n === 1 ? `Jede Woche am ${days}` : `Alle ${n} Wochen am ${days}`;
      break;
    }
    case 'MONTHLY_NTH_WEEKDAY': {
      const days = joinGerman(sortWeekdays(rule));

      // "Jeden 1. Mittwoch und letzten Freitag" would be ambiguous with several
      // ordinals, so only the single-ordinal case gets the attributive phrasing
      if (rule.ordinals.length === 1) {
        const ordinal = sortOrdinals(rule, ORDINAL_ATTRIBUTIVE_LABELS)[0];
        text = n === 1
          ? `Jeden ${ordinal} ${days} im Monat`
          : `Alle ${n} Monate am ${ordinal} ${days}`;
      } else {
        const ordinals = joinGerman(sortOrdinals(rule, ORDINAL_NOUN_LABELS));
        const rhythm = n === 1 ? 'Jeden Monat' : `Alle ${n} Monate`;
        text = `${rhythm} am ${days} — ${ordinals} Vorkommen`;
      }
      break;
    }
    case 'MONTHLY_DAY_OF_MONTH': {
      const days = joinGerman([...rule.daysOfMonth].sort((a, b) => a - b).map(d => `${d}.`));
      text = n === 1 ? `Jeden Monat am ${days}` : `Alle ${n} Monate am ${days}`;
      break;
    }
    case 'YEARLY':
      text = n === 1 ? 'Jedes Jahr am Datum des Serienstarts' : `Alle ${n} Jahre am Datum des Serienstarts`;
      break;
    default:
      text = EVENT_RECURRENCE_TYPE_LABELS[rule.type];
      break;
  }

  if (until !== undefined && until !== null && until.length > 0) {
    return `${text}, bis ${formatRFCDate(until)}`;
  }

  return text;
}

// Combines a date (yyyy-MM-dd) and a time (HH:mm) from two form inputs into an
// RFC3339 string in the browser's timezone. The backend re-evaluates the local
// time-of-day in Europe/Berlin, which is what the admins work in.
//
// Returns null when the two inputs don't form a valid instant. Do NOT "simplify" this
// back to a plain toISOString(): a date/time input can be empty or half-typed, and this
// runs during render (see buildEventSeriesUpdate), so it must never throw.
export function toRFC3339(date: string, time: string): string | null {
  if (date.length === 0 || time.length === 0) return null;

  const parsed = new Date(`${date}T${time}`);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString();
}

// Splits an RFC3339 string into the yyyy-MM-dd / HH:mm values a date+time input pair needs
export function toDateInputValue(iso: string): string {
  const d = new Date(iso);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

export function toTimeInputValue(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function formatEventDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// ---------------------------------------------------------------------------
// Reminder mails: `reminderMinutes` is a list of minute-offsets before an
// event's start at which the backend sends reminder mails ([] = disabled).
// The form edits it as rows of number + unit; these helpers convert both ways.
// ---------------------------------------------------------------------------

export type ReminderUnit = 'MINUTES' | 'HOURS' | 'DAYS';

// One row of the reminder editor - the raw input, may be empty while typing
export interface ReminderMinutesRow {
  value: string;
  unit: ReminderUnit;
}

// Hard limits of the backend (models/event.go: MaxEventReminderCount/-Minutes)
export const MAX_EVENT_REMINDER_COUNT = 10;
export const MAX_EVENT_REMINDER_MINUTES = 30 * 24 * 60;

export const REMINDER_UNIT_FACTORS: Record<ReminderUnit, number> = {
  MINUTES: 1,
  HOURS: 60,
  DAYS: 1440
};

export const REMINDER_UNIT_LABELS: Record<ReminderUnit, string> = {
  MINUTES: 'Minuten',
  HOURS: 'Stunden',
  DAYS: 'Tage'
};

// Render order for the csv help table, largest unit first
export const ALL_REMINDER_UNITS: ReminderUnit[] = ['DAYS', 'HOURS', 'MINUTES'];

// Accepted unit spellings of the csv "Erinnerungen" column; a bare number means minutes.
// Case-insensitive, space and a trailing dot are optional ("2 Std." == "2std").
// MUST BE SYNCED WITH THE BACKEND: models/eventImport.go -> eventReminderUnitFactors
export const EVENT_IMPORT_REMINDER_UNIT_ALIASES: Record<ReminderUnit, string[]> = {
  MINUTES: ['m', 'min', 'Minute', 'Minuten'],
  HOURS:   ['h', 'Std', 'Stunde', 'Stunden'],
  DAYS:    ['d', 't', 'Tag', 'Tage', 'Tagen'],
};

// Picks the largest unit that divides evenly: 1440 -> 1 day, 120 -> 2 hours, 90 -> 90 minutes
function largestFittingUnit(minutes: number): ReminderUnit {
  if (minutes % REMINDER_UNIT_FACTORS.DAYS === 0) return 'DAYS';
  if (minutes % REMINDER_UNIT_FACTORS.HOURS === 0) return 'HOURS';
  return 'MINUTES';
}

export function minutesToReminderRow(minutes: number): ReminderMinutesRow {
  const unit = largestFittingUnit(minutes);
  return { value: String(minutes / REMINDER_UNIT_FACTORS[unit]), unit };
}

// Seeds the editor rows from a stored offset list, largest offset first
export function reminderMinutesToRows(minutes: number[] | undefined): ReminderMinutesRow[] {
  return [...(minutes ?? [])].sort((a, b) => b - a).map(minutesToReminderRow);
}

// Parses and validates the editor rows into the canonical (descending) offset list.
// Returns a German error message instead when a row is empty/invalid, an offset is out
// of the backend's 1..30-days range, or two rows resolve to the same offset.
export function reminderRowsToMinutes(rows: ReminderMinutesRow[]): { minutes: number[] } | { error: string } {
  const minutes: number[] = [];

  for (const row of rows) {
    const parsed = Number(row.value);
    if (row.value.trim().length === 0 || !Number.isInteger(parsed)) {
      return { error: 'Bitte geben Sie für jede Erinnerung eine gültige Zahl ein' };
    }

    const total = parsed * REMINDER_UNIT_FACTORS[row.unit];
    if (total < 1 || total > MAX_EVENT_REMINDER_MINUTES) {
      return { error: 'Erinnerungen müssen zwischen 1 Minute und 30 Tagen vor Beginn liegen' };
    }

    if (minutes.includes(total)) {
      return { error: 'Zwei Erinnerungen haben denselben Zeitpunkt' };
    }

    minutes.push(total);
  }

  minutes.sort((a, b) => b - a);
  return { minutes };
}

export function formatReminderOffset(minutes: number): string {
  const unit = largestFittingUnit(minutes);
  const amount = minutes / REMINDER_UNIT_FACTORS[unit];
  if (unit === 'DAYS') return amount === 1 ? '1 Tag' : `${amount} Tage`;
  if (unit === 'HOURS') return `${amount} Std.`;
  return `${amount} Min.`;
}

// "1 Tag, 3 Std., 45 Min." - largest offset first
export function formatReminderOffsetList(minutes: number[]): string {
  return [...minutes].sort((a, b) => b - a).map(formatReminderOffset).join(', ');
}

// Compares two RFC3339 strings by instant, not by text - the backend returns nanosecond
// precision with an offset while the form produces a UTC millisecond string
function sameInstant(a: string, b: string): boolean {
  return new Date(a).getTime() === new Date(b).getTime();
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

export function recurrenceRulesEqual(a: EventRecurrenceRule, b: EventRecurrenceRule): boolean {
  return a.type === b.type &&
    a.interval === b.interval &&
    arraysEqual(a.weekdays, b.weekdays) &&
    arraysEqual(a.ordinals, b.ordinals) &&
    arraysEqual(a.daysOfMonth, b.daysOfMonth);
}

// null means "public", so a null/non-null mismatch is a real change
export function featureFilterEqual(a: EventFeatureFilter | null, b: EventFeatureFilter | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;

  return arraysEqual(a.features, b.features) &&
    a.excludeCommunityMembers === b.excludeCommunityMembers &&
    a.onlyCommunityMembers === b.onlyCommunityMembers;
}

// The form state of the event-series edit dialog
export interface EventSeriesFormValues {
  subject: string;
  description: string;
  link: string;
  startDate: string;
  startTime: string;
  endTime: string;
  until: RFC3339Date;
  rule: EventRecurrenceRule;
  featureFilter: EventFeatureFilter | null;
  reminderMinutes: number[] | null; // null = the reminder editor currently holds an unparseable row
}

// Builds a PARTIAL update payload for an event series - only fields that genuinely changed.
//
// This is not an optimisation. The backend decides whether to delete and rebuild every
// future occurrence from the mere *presence* of start/end/until/rule in the body
// (api/handler/eventSeries.go: `regenerate := b.Start.IsSet() || b.End.IsSet() || ...`).
// Sending an unchanged field would therefore discard individual edits to future
// occurrences and reassign all their event IDs on every save.
//
// Kept pure and outside the component so it can be tested directly, and because the
// dialog calls it during render to drive the "will regenerate" warning.
export function buildEventSeriesUpdate(form: EventSeriesFormValues, series: EventSeries): UpdateEventSeriesRequest {
  const request: UpdateEventSeriesRequest = {};

  if (form.subject.trim() !== series.subject) request.subject = form.subject.trim();
  if (form.description.trim() !== series.description) request.description = form.description.trim();
  if (form.link.trim() !== series.link) request.link = form.link.trim();

  // An emptied input is not a change - submitting is blocked by the dialog's guards anyway
  if (form.until.length > 0 && form.until !== series.until) request.until = form.until;

  if (!recurrenceRulesEqual(form.rule, series.rule)) request.rule = form.rule;

  // start and end share the date, so a date change has to move both - otherwise the
  // occurrence duration (end - start) would be computed against the old date.
  // They are treated as one unit: if either half is unparseable (a field being edited),
  // neither is sent, so the backend never sees a half-moved window.
  const nextStart = toRFC3339(form.startDate, form.startTime);
  const nextEnd = toRFC3339(form.startDate, form.endTime);
  if (nextStart !== null && nextEnd !== null) {
    if (!sameInstant(nextStart, series.start)) request.start = nextStart;
    if (!sameInstant(nextEnd, series.end)) request.end = nextEnd;
  }

  if (!featureFilterEqual(form.featureFilter, series.featureFilter)) request.featureFilter = form.featureFilter;

  // null = invalid editor input, not a change (submitting is blocked by the dialog's guards)
  if (form.reminderMinutes !== null && !arraysEqual(form.reminderMinutes, series.reminderMinutes ?? [])) {
    request.reminderMinutes = form.reminderMinutes;
  }

  return request;
}

// Mirrors the backend's regenerate condition, so the dialog's warning matches reality
export function updateRegeneratesOccurrences(request: UpdateEventSeriesRequest): boolean {
  return request.start !== undefined ||
    request.end !== undefined ||
    request.until !== undefined ||
    request.rule !== undefined;
}
